import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NextArrow } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as PreviousArrow } from '../../assets/icons/prev-arrow.svg';
import SelectField from '../select-field/SelectField';

interface DateStatus {
  date: number;
  backgroundColor: string;
  borderColor: string;
}

interface CalendarProperties {
  dateStatusData: DateStatus[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  handleMonthOrYearChange: (month: number, year: number) => void;
  monthAndYear: { month: string; year: string };
  setMonthAndYear: React.Dispatch<React.SetStateAction<{ month: string; year: string }>>;
  keyToLookupUnderData?: string | null; // Key: Value to fetch data under each date
  defaultValueToShow?: string | null; // Optional default value to show under each date
  dataLoading: boolean; // Optional loading state for the data
}

/**
 * HeatMap calendar view
 * @param {any} properties Component properties
 * @returns {any} heatmap calendar
 */
function HeatMapCalendar(properties: CalendarProperties) {
  const {
    dateStatusData,
    selectedDate,
    setSelectedDate,
    handleMonthOrYearChange,
    monthAndYear,
    setMonthAndYear,
    keyToLookupUnderData,
    defaultValueToShow = null,
    dataLoading = false,
  } = properties;
  const { i18n } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /**
   * Change view prev month
   */
  const previousMonth = () => {
    const newDate = new Date(currentYear, currentMonth - 1, 1);
    setCurrentDate(newDate);
    setMonthAndYear({
      month: newDate.getMonth().toString(),
      year: newDate.getFullYear().toString(),
    });
  };

  /**
   * Change view to month
   */
  const nextMonth = () => {
    const newDate = new Date(currentYear, currentMonth + 1, 1);
    setCurrentDate(newDate);
    setMonthAndYear({
      month: newDate.getMonth().toString(),
      year: newDate.getFullYear().toString(),
    });
  };

  /**
   * Function to get days in a single month
   * @param {any} year year
   * @param {any} month month
   * @returns {any} number of days
   */
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  /**
   * Function to get first date of the month
   * @param {any} year year
   * @param {any} month month
   * @returns {any} first date
   */
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  /**
   * Function to generate days to the specific month
   * @returns {any} days array
   */
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let index = 0; index < firstDayOfMonth; index += 1) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day += 1) {
      days.push(day);
    }

    return days;
  };

  /**
   * Get status / color of the data
   * @param {any} day day
   * @returns {any} date colors
   */
  const getDateColors = (day: number) => dateStatusData.find((item) => item?.date === day) || null;

  /**
   * Handle month change
   * @param {any} name month
   * @param {any} value selected value
   */
  const handleMonthChange = (name: string, value: string) => {
    setMonthAndYear((previousValue) => ({
      ...previousValue,
      month: value,
    }));
    setCurrentDate(new Date(currentYear, parseInt(value, 10), 1));
  };

  /**
   * Handle year change
   * @param {any} name year
   * @param {any} value selected value
   */
  const handleYearChange = (name: string, value: string) => {
    setMonthAndYear((previousValue) => ({
      ...previousValue,
      year: value,
    }));
    setCurrentDate(new Date(parseInt(value, 10), currentMonth, 1));
  };

  /**
   * Format date as "DD Month YYYY"
   * @param {any} date Date object
   * @returns {any} formatted date string
   */
  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = MONTHS[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  /**
   * Handle day click
   * @param {any} day day
   */
  const handleDayClick = (day: number | null) => {
    if (day) {
      const newSelectedDate = new Date(currentYear, currentMonth, day);
      setSelectedDate(newSelectedDate);
    }
  };

  /**
   * Function to return the date is selected date or not
   * @param {any} day day
   * @returns {any} boolean true if same date
   */
  const isSameDate = (day: number): boolean => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth &&
      selectedDate.getFullYear() === currentYear
    );
  };

  useEffect(() => {
    if (monthAndYear.month && monthAndYear.year) {
      handleMonthOrYearChange(Number(monthAndYear.month) + 1, Number(monthAndYear.year));
    }
  }, [monthAndYear]);

  return (
    <div className="heatMap-calendar-warp">
      <div className="heatMap-calendar-header">
        <h6>{formatDate(selectedDate)}</h6>
        <div className="heatMap-month-year-selection">
          <SelectField
            name="month"
            inputLabel=""
            selectedValue={monthAndYear.month}
            placeholder="Month"
            onChangeValue={handleMonthChange}
            valueRequired
            options={MONTHS.map((month, index) => ({
              label: month,
              value: index.toString(),
            }))}
            disabledAutoSorting
          />
          <SelectField
            name="year"
            inputLabel=""
            selectedValue={monthAndYear.year}
            placeholder="Year"
            onChangeValue={handleYearChange}
            valueRequired
            options={Array.from({ length: 10 }, (_, index) => {
              const year = currentYear - index;
              return { label: year.toString(), value: year.toString() };
            })}
            disabledAutoSorting
          />
        </div>
      </div>
      <div className="heatMap-calendar-container">
        {dataLoading && (
          <div className="loader-container">
            <div className="lds-dual-ring" />
          </div>
        )}
        <div className="calendar-month-nav">
          <div className="calendar-month-title">
            {MONTHS[currentMonth]} {currentYear}
          </div>

          <div className="calendar-nav-buttons">
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={previousMonth}
              style={{
                transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
              <PreviousArrow />
            </button>
            <button
              type="button"
              className="calendar-nav-btn"
              onClick={nextMonth}
              style={{
                transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
              <NextArrow />
            </button>
          </div>
        </div>

        <div className="calendar-days-header">
          {DAYS.map((day) => (
            <div key={day} className="calendar-day-name">
              {day}
            </div>
          ))}
        </div>

        <div className="calendar-grid">
          {generateCalendarDays().map((day, index) => {
            let dateToDisplay = (
              <div className="calendar-day-container">
                <button
                  type="button"
                  key={index}
                  className={`calendar-day ${isSameDate(day || 0) ? 'selected' : ''} ${!day ? 'invisible' : ''}`}
                  onClick={() => handleDayClick(day)}
                  disabled={!day}>
                  {day}
                </button>
                {day && keyToLookupUnderData && <p>{defaultValueToShow || ''}</p>}
              </div>
            );
            if (day) {
              const foundedDate: any = getDateColors(day);
              if (foundedDate) {
                dateToDisplay = (
                  <div className="calendar-day-container">
                    <button
                      type="button"
                      key={index}
                      className={`calendar-day ${isSameDate(day) ? 'selected' : ''}`}
                      style={
                        {
                          '--date-background-color': foundedDate.backgroundColor,
                          '--date-border-color': foundedDate.borderColor,
                        } as React.CSSProperties
                      }
                      onClick={() => handleDayClick(day)}
                      disabled={!day}>
                      {day}
                    </button>
                    {day && keyToLookupUnderData && (
                      <p title={foundedDate?.[keyToLookupUnderData] || ''}>
                        {foundedDate?.[keyToLookupUnderData] || ''}
                      </p>
                    )}
                  </div>
                );
              }
            }
            return dateToDisplay;
          })}
        </div>
      </div>
    </div>
  );
}

export default HeatMapCalendar;
